'use client';

import { IconMailCustomerSupport, IconPhoneCustomerSupport } from '@vs-shop/ui/icons';
import type { ClassValue } from 'clsx';
import type { CustomerSupportT } from '../../../type/customerSupport';
import {
  SupportCard,
  SupportCardAvatar,
  SupportCardContact,
  SupportCardContactTextVariant,
  SupportCardContactVariant,
  SupportCardContactMark,
  SupportCardContactText,
  SupportCardHours,
  SupportCardName,
  SupportCardPhoto,
  SupportCardPresence,
  SupportCardPresenceState,
  type SupportCardEdge,
  type SupportCardGround,
} from '@vs-shop/commerce-ui/supportCard';

import { clsxm } from '@vs-shop/utils';
import { useIntl } from 'react-intl';

import { customerSupportNameConcat } from '../../../utils/customerSupportNameConcat';

import ImageSupportProfilePicture from '../../../assets/customerSupport/order-customer-support.png';

type Props = {
  customerSupport: CustomerSupportT;
  isCustomerSupportOnline: boolean;
  /**
   * Which arrangement of the row this is: a checkout step drops the e-mail
   * address — the shopper is mid-order and the telephone is the only channel
   * that answers now — and opens the telephone row up to match the type around
   * it.
   */
  variant: 'classic' | 'checkout';
  /** The ground the card itself sits on. */
  ground: SupportCardGround;
  edge: SupportCardEdge;
  className?: ClassValue;
};

/** The advisor's card as this storefront composes it, in a row. */
export default function CustomerSupportSmall({
  customerSupport,
  isCustomerSupportOnline,
  variant,
  ground,
  edge,
  className,
}: Props) {
  const intl = useIntl();

  const { email, openingHoursShort, telReadable } = customerSupport;

  const contactVariant =
    variant === 'checkout' ? SupportCardContactVariant.CHECKOUT : SupportCardContactVariant.CLASSIC;
  const contactTextVariant =
    variant === 'checkout'
      ? SupportCardContactTextVariant.READING
      : SupportCardContactTextVariant.COMPACT;

  const presence = isCustomerSupportOnline
    ? SupportCardPresenceState.ONLINE
    : SupportCardPresenceState.OFFLINE;

  return (
    <SupportCard
      variant="compact"
      ground={ground}
      edge={edge}
      hang="seated"
      className={clsxm(className)}
    >
      {/* IMAGE */}
      <SupportCardAvatar variant="compact" surround="unframed" presence={presence}>
        <SupportCardPhoto
          src={ImageSupportProfilePicture.src}
          alt={customerSupportNameConcat(customerSupport)}
          preload
          width={64}
          height={64}
        />
        <SupportCardPresence variant="pinned" presence={presence} />
      </SupportCardAvatar>

      <div className="@container min-w-0 flex-1">
        <div
          className={clsxm(
            'flex flex-col justify-between gap-5',
            '@[350px]:flex-row @[350px]:justify-between'
          )}
        >
          {/* NAME, OPENING HOURS */}
          <div className="flex flex-col gap-1.5">
            <SupportCardName>{customerSupportNameConcat(customerSupport)}</SupportCardName>
            <SupportCardHours variant="compact">{openingHoursShort}</SupportCardHours>
          </div>

          {/* PHONE, EMAIL */}
          <div className="flex flex-col gap-3">
            <SupportCardContact
              href={`tel:${customerSupport.telValue}`}
              title={intl.formatMessage({
                id: 'customerSupport.phone',
                defaultMessage: 'RCprofi zákaznická podpora telefon',
              })}
              variant={contactVariant}
            >
              <SupportCardContactMark
                variant={contactVariant}
                render={<IconPhoneCustomerSupport />}
              />
              <SupportCardContactText variant={contactTextVariant}>
                {telReadable}
              </SupportCardContactText>
            </SupportCardContact>
            {variant === 'checkout' ? null : (
              <SupportCardContact
                href={`mailto:${customerSupport.email}`}
                title={intl.formatMessage({
                  id: 'customerSupport.email',
                  defaultMessage: 'RCprofi zákaznická podpora e-mail',
                })}
                variant={SupportCardContactVariant.CLASSIC}
              >
                <SupportCardContactMark
                  variant={SupportCardContactVariant.CLASSIC}
                  render={<IconMailCustomerSupport />}
                />
                <SupportCardContactText variant={SupportCardContactTextVariant.COMPACT}>
                  {email}
                </SupportCardContactText>
              </SupportCardContact>
            )}
          </div>
        </div>
      </div>
    </SupportCard>
  );
}
